import { useEffect, useState } from 'react'
import { Loader2, Paperclip } from 'lucide-react'

import Modal from '@/components/common/Modal'
import {
  getIOFlowReference,
  listIOFlowReferences,
  type IOFlowReference,
  type IOFlowReferenceKind,
} from '@/api/integration'

interface Props {
  project: string
  open: boolean
  onClose: () => void
  /** Attaches the fetched definition to the prompt as a text attachment. */
  onAttach: (text: string, name: string) => void
}

const KINDS: { key: IOFlowReferenceKind; label: string; hint: string }[] = [
  { key: 'flow', label: 'Existing flows', hint: 'Flows already built in IOFlow' },
  { key: 'example', label: 'Prebuilt examples', hint: "IOFlow's example library" },
  { key: 'app', label: 'Applications', hint: 'A connector and its services/events' },
]

/**
 * Attach an IOFlow application, flow or prebuilt example to the prompt, so the
 * agent can be told "build one like this".
 *
 * Deliberately no new attachment plumbing: the fetched JSON goes through
 * `useAttachments.addText`, which already normalises, stages to the CLI and
 * works on every surface. A reference is just text the agent can read.
 */
export default function IOFlowReferencePicker({ project, open, onClose, onAttach }: Props) {
  const [kind, setKind] = useState<IOFlowReferenceKind>('flow')
  const [items, setItems] = useState<IOFlowReference[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError('')
    listIOFlowReferences(project, kind)
      .then(setItems)
      .catch((e) => setError(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false))
  }, [open, kind, project])

  const attach = async (item: IOFlowReference) => {
    setBusyId(item.id)
    setError('')
    try {
      const ref = await getIOFlowReference(project, kind, item.id)
      const slug = (item.name || item.id).replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()
      onAttach(JSON.stringify(ref, null, 2), `ioflow-${kind}-${slug}.json`)
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail || String(e))
    } finally {
      setBusyId('')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Attach an IOFlow reference">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
          Attach an existing flow, example or application so you can say “build
          one like this”.
        </p>

        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          {KINDS.map((k) => (
            <button
              key={k.key}
              className={kind === k.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              onClick={() => setKind(k.key)}
              title={k.hint}
            >
              {k.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.9375rem' }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
            Nothing here yet.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '20rem', overflowY: 'auto' }}>
          {items.map((item) => (
            <button
              key={item.id}
              className="btn btn-secondary btn-sm"
              disabled={!!busyId}
              onClick={() => attach(item)}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                whiteSpace: 'normal',
                height: 'auto',
                lineHeight: 1.4,
              }}
            >
              {busyId === item.id ? (
                <Loader2 size={12} className="spin" style={{ flexShrink: 0 }} />
              ) : (
                <Paperclip size={12} style={{ flexShrink: 0 }} />
              )}
              <span style={{ minWidth: 0 }}>
                {item.name || item.id}
                {item.status ? (
                  <span style={{ color: 'var(--color-text-secondary)' }}> · {item.status}</span>
                ) : null}
                {item.category ? (
                  <span style={{ color: 'var(--color-text-secondary)' }}> · {item.category}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div style={{ fontSize: '0.9375rem', color: 'var(--color-error, #f44336)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}
